import querystring from 'querystring';
import PATHS from './paths';
import { CDN_DOMAIN } from 'client/settings';
import modelCodes from 'client/constants/modelCode';

const REGIONS = {
  US: PATHS.US_BASE,
  CN: PATHS.CN_BASE
};

/*
Input: a=b454&c=dhjjh&f=g6hksdfjlksd
Return: {"a":"b454","c":"dhjjh","f":"g6hksdfjlksd"}
*/
const parsingURL = (url = '') => querystring.parse(url.replace(/\?/g, ''));

const getQueryString = (params, allowEmptyValue = false) => {
  if (!params) {
    return '';
  }

  const obj = {};
  Object.keys(params).forEach(key => {
    const value = params[key];
    if (value !== undefined && value !== null && (allowEmptyValue || value)) {
      obj[key] = value;
    }
  });

  return new URLSearchParams(obj).toString();
};

const getRegion = () => {
  let region = 'US';

  if (typeof window !== 'undefined') {
    region = window.location.pathname.split('/')[1].toUpperCase();
  }

  return region;
};

// Returns a path in the same region as the current url.
// Replace the dynamic params with provided values, if applicable
const getSameRegionPath = (path, params) => {
  // pathname gives 'us/path' from 'http://example.com/us/path'
  const { pathname } = window.location;
  const pathArray = pathname.split('/');
  let prefix;

  if (pathArray.length > 0) {
    prefix = `/${pathArray[0]}`;
  }
  if (!Object.values(REGIONS).includes(prefix)) {
    prefix = PATHS.US_BASE;
  }

  let regionPath = `${prefix}${path}`;

  if (params) {
    Object.keys(params).forEach((k) => {
      regionPath = regionPath.replace(`:${k}`, params[k]);
    });
  }

  return regionPath;
}

const getPageNavigation = (pageName, currentLocation) => {
  if (!currentLocation && typeof window === 'undefined') return {};

  const location = currentLocation || window.location;
  const region = location.pathname.split('/')[1] || 'us';
  const year = location.pathname.split('/')[3] || '2019';
  const modelCode = location.pathname.split('/')[4] || 'F91U36APALL';
  const queryParams = location.search;
  const pageUrlPrefix = `/${region}/configurator/${year}/${modelCode}/`;
  const sections = {
    model: {
      prevSection: '',
      nextSection: `${pageUrlPrefix}exterior${queryParams}`
    },
    exterior: {
      prevSection: `${pageUrlPrefix}model${queryParams}`,
      nextSection: `${pageUrlPrefix}interior${queryParams}`
    },
    interior: {
      prevSection: `${pageUrlPrefix}exterior${queryParams}`,
      nextSection: `${pageUrlPrefix}summary${queryParams}`
    },
    summary: {
      prevSection: `${pageUrlPrefix}interior${queryParams}`,
      nextSection: `${pageUrlPrefix}confirmation${queryParams}`
    }
  };

  return sections[pageName || 'model'];
};

const getPageProduct = (modelCode) => {
  const pathArray = window.location.pathname.split('/');
  const baseUrl = pathArray[0];
  const region = pathArray[1];
  let modelPath = '';
  if (modelCode === modelCodes.FF91Futurist.fullModelCode) {
    modelPath = 'ff-91-Futurist/';
  } else if (modelCode === modelCodes.FF91FuturistAlliance.fullModelCode) {
    modelPath = 'ff-91-Alliance/'
  }

  return `${baseUrl}/${region}/product/${modelPath}`;
}

const getCDNDomain = () => getRegion() === 'CN' ? 'genesis-cdn.faradayfuturecn.com' : 'genesis-cdn.ff.com';

const getCDNDomainV2 = () => CDN_DOMAIN;

const getLoadingLogo = () => `https://${getCDNDomain()}/ff-favicon.png`

const isAppOnly = () => {
  if (typeof window !== 'undefined') {
    const queryStringParameters = parsingURL(window.location?.search);

    return (queryStringParameters.APPONLY || '0') === '1'
  }

  return false;
}

// NEW Configurator Navigation
const getNewPageNavigation = (pageName) => {
  const { location } = window;
  const region = location.pathname.split('/')[1] || 'us';
  const year = location.pathname.split('/')[3] || '2022';
  const modelCode = location.pathname.split('/')[4] || 'FF91FNA-ALLIANCE';
  const queryParams = location.search;
  const pageUrlPrefix = `/${region}/design/${year}/${modelCode}/`;
  const sections = {
    model: {
      prevSection: '',
      targetSection: `${pageUrlPrefix}model${queryParams}`,
      nextSection: `${pageUrlPrefix}exterior${queryParams}`
    },
    exterior: {
      prevSection: `${pageUrlPrefix}model${queryParams}`,
      targetSection: `${pageUrlPrefix}exterior${queryParams}`,
      nextSection: `${pageUrlPrefix}wheels${queryParams}`
    },
    wheels: {
      prevSection: `${pageUrlPrefix}exterior${queryParams}`,
      targetSection: `${pageUrlPrefix}wheels${queryParams}`,
      nextSection: `${pageUrlPrefix}interior${queryParams}`
    },
    interior: {
      prevSection: `${pageUrlPrefix}wheels${queryParams}`,
      targetSection: `${pageUrlPrefix}interior${queryParams}`,
      nextSection: `${pageUrlPrefix}summary${queryParams}`
    },
    summary: {
      prevSection: `${pageUrlPrefix}interior${queryParams}`,
      targetSection: `${pageUrlPrefix}summary${queryParams}`,
      nextSection: `${pageUrlPrefix}summary/confirmation${queryParams}`
    },
    confirmation: {
      prevSection: `${pageUrlPrefix}summary${queryParams}`,
      targetSection: `${pageUrlPrefix}summary/confirmation${queryParams}`,
      nextSection: ''
    }
  };

  return sections[pageName || 'model'];
};

const getPageNavigationV3 = (pageName, optionsParams) => {
  const { location } = window;
  const region = location.pathname.split('/')[1] || 'us';
  const year = location.pathname.split('/')[3] || '2022';
  const modelCode = location.pathname.split('/')[4] || 'FF91FNA-ALLIANCE';
  const pageUrlPrefix = `/${region}/design/${year}/${modelCode}/${pageName}`;

  let params = '';
  if (optionsParams) {
    params = new URLSearchParams(optionsParams);
    // remove undefined or null params
    for (const key in optionsParams) {
      if (optionsParams[key] === undefined || optionsParams[key] === null) {
        params.delete(key);
      }
    }
    params = params.toString();
  }
  return `${pageUrlPrefix}?${params}`;
};

const getPageBackNavigationV3 = (optionsParams) => {
  const pageName = getCurrentPageV3();

  const entryPage = optionsParams.ENTRY_PAGE;

  // currently, the entry page can be model and options only
  if (pageName === 'model' || (pageName === 'options' && pageName == entryPage)) {
    return null;
  } else if (pageName === 'summary') {
    return getPageNavigationV3('options', { ...optionsParams, APP_GOBACK_FROM: 'summary' });
  } else {
    return getPageNavigationV3('model', { ...optionsParams, APP_GOBACK_FROM: 'options' });
  }
}

const getCurrentPageV3 = () => {
  const { location } = window;
  const pageName = location.pathname.split('/')[5];

  return pageName;
}

export default {
  parsingURL,
  getQueryString,
  getRegion,
  getPageNavigation,
  getPageProduct,
  getCDNDomain,
  getCDNDomainV2,
  getLoadingLogo,
  isAppOnly,
  getNewPageNavigation,
  getSameRegionPath,
  getPageNavigationV3,
  getPageBackNavigationV3,
  getCurrentPageV3,
};
